import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';

import Button from 'components/Button';
import FormContainer from 'components/LoanForm/FormContainer';
import FormNavigation from 'components/FormNavigation';
import InputSelect from 'components/InputSelect';
import MonthYearPicker from 'components/MonthYearPicker';
import NumberInput, { NumberInputHintVariant } from 'components/NumberInput';
import CheckboxSmall from 'components/Checkbox/CheckboxSmall';
import Input from 'components/Input';
import IncomeDisclaimerNote from 'components/Common/IncomeDisclaimerNote/IncomeDisclaimerNote';

import { CurrentFlow } from 'enums/CurrentFlow';
import { YourIncomeResult } from 'enums/FlowNextResults';
import { YourIncomeVariable } from 'enums/LoanFormVariables';
import { PARTNER_DATA, PartnerType } from 'enums/PartnerName';

import { setYourIncomeData, YourIncomeVariant } from 'handlers/yourIncome';
import { PayFrequencyOptionValue } from 'handlers/yourTotalIncome';

import { FlowComponentType } from 'routes/types';

import { getApplicationStep } from 'selectors/getApplicationStep';
import { getPreQualificationData } from 'selectors/preQualificationData';
import { getYourIncome, getYourIncomeVariant } from 'selectors/yourIncome';
import { getYourNameData } from 'selectors/yourName';

import { formatMonthYear, getYearsFromNow } from 'utils/dateUtils';
import { getMessageForRequiredFields } from 'utils/errors';
import { ConversionType, trackConversion } from 'utils/analytics';
import { getParsedNumericValue } from 'utils/getParsedNumericValue';
import { getHighestTierCredential } from 'utils/credentialUtils';

import {
  ANNUAL_INCOME_MAX_LENGTH,
  LOW_ANNUAL_INCOME_WARNING,
  PAY_FREQUENCY_OPTIONS,
  LowAnnualIncomeConfirmation,
  getLowAnnualIncomeConfirmation,
  isLowAnnualIncome,
} from 'components/Verification/Steps/EmploymentDetails/employmentDetailsUtils';

import { FINWISE_PRIVACY_NOTICE_URL } from 'constants/urls';

import LowAnnualIncomeConfirmModal, { LowAnnualIncomeChoice } from './LowAnnualIncomeConfirmModal';

import styles from './YourIncome.module.scss';

enum YourIncomeInputLabel {
  CreditScoreRange = 'Approximate credit score',
  GrossAnnualIncome = 'Gross annual income',
  StartOfEmployment = 'Start date of primary employer',
  EmployerName = 'Primary employer name',
  JobTitle = 'Job title',
  PayFrequency = 'Pay frequency',
  NotCurrentlyEmployed = 'Not currently employed',
  CredentialAcquisitionDate = 'Date you first got your [user-credentials] credential',
}

const replaceCredentialPlaceholder = (label: string, credential: string) =>
  label.replace('[user-credentials]', credential);

const YourIncome = ({ handleNext, navigationInfo }: FlowComponentType) => {
  const dispatch = useDispatch();
  const variant = useSelector(getYourIncomeVariant);
  const { currentFlow } = useSelector(getApplicationStep);
  const { partnerId: partnerName, benefit } = useSelector(getPreQualificationData);
  const defaultValues = useSelector(getYourIncome);
  const { credentials } = useSelector(getYourNameData);
  const minDate = getYearsFromNow(-50);
  const maxDate = getYearsFromNow(0);
  const shouldShowCredentialAcquisitionDate = !!credentials && variant !== YourIncomeVariant.Default;
  const shouldShowAlimonyNote =
    variant !== YourIncomeVariant.CardWaitlist && currentFlow !== CurrentFlow.StudentLoanForgiveness;

  const {
    register,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      ...defaultValues,
      ...(partnerName &&
        PARTNER_DATA[partnerName].type === PartnerType.Employer && {
          employer_name: PARTNER_DATA[partnerName].fullName,
        }),
    },
  });

  const watcher = watch();
  const annualIncomeInputRef = useRef<HTMLInputElement | null>(null);
  const [isLowIncomeConfirmOpen, setIsLowIncomeConfirmOpen] = useState(false);
  const lowIncomeConfirmation = getLowAnnualIncomeConfirmation(
    watcher[YourIncomeVariable.TotalAnnualIncome],
    watcher[YourIncomeVariable.PayFrequency],
  );

  const initialPartner = benefit && partnerName ? PARTNER_DATA[partnerName] : null;
  const benefitEligibleTitles = initialPartner?.benefitEligibleTitles ?? [];

  const shouldCollectJobTitle = benefitEligibleTitles.length > 0;

  useEffect(() => {
    if (initialPartner && !!initialPartner.paymentFrequency) {
      setValue(YourIncomeVariable.PayFrequency, initialPartner.paymentFrequency);
    }
  }, [initialPartner]);

  const persistAndContinue = (totalAnnualIncome = watcher[YourIncomeVariable.TotalAnnualIncome]) => {
    dispatch(
      setYourIncomeData({
        ...defaultValues,
        ...watcher,
        [YourIncomeVariable.TotalAnnualIncome]: totalAnnualIncome,
        [YourIncomeVariable.CredentialAcquisitionDate]: shouldShowCredentialAcquisitionDate
          ? watcher[YourIncomeVariable.CredentialAcquisitionDate] ??
            defaultValues[YourIncomeVariable.CredentialAcquisitionDate]
          : defaultValues[YourIncomeVariable.CredentialAcquisitionDate],
      }),
    );

    if (variant === YourIncomeVariant.CardWaitlist) {
      const creditScoreRange = watcher[YourIncomeVariable.CreditScoreRange];
      const startOfEmployment = watcher[YourIncomeVariable.StartOfEmployment];

      const startDate = new Date(startOfEmployment ?? '');
      const now = new Date();
      const tenureMonths = (now.getFullYear() - startDate.getFullYear()) * 12 + now.getMonth() - startDate.getMonth();

      const mayQualifyForLoan =
        // (Number(creditScoreRange) >= 580 && tenureMonths >= 12) ||
        Number(creditScoreRange) >= 650 && tenureMonths >= 6;

      analytics.track('Income Data Collected', {
        creditScoreRange,
        totalAnnualIncome,
        employerName: watcher[YourIncomeVariable.EmployerName],
        startOfEmployment,
        notEmployed: watcher[YourIncomeVariable.NotEmployed],
        tenureMonths,
        mayQualifyForLoan,
      });

      if (mayQualifyForLoan) {
        trackConversion(ConversionType.CardMayQualifyForLoan);
      }

      handleNext(YourIncomeResult.CardWaitlist);
    } else {
      handleNext(variant === YourIncomeVariant.CardCrossSell ? YourIncomeResult.CardCrossSell : undefined);
    }
  };

  const handleContinue = () => {
    if (lowIncomeConfirmation) {
      setIsLowIncomeConfirmOpen(true);
      return;
    }

    persistAndContinue();
  };

  const handleLowIncomeConfirm = (choice: LowAnnualIncomeChoice, confirmation: LowAnnualIncomeConfirmation) => {
    setIsLowIncomeConfirmOpen(false);

    // "Other" means neither amount is their annual income, so send them back to re-enter it.
    if (choice === LowAnnualIncomeChoice.Other) {
      setValue(YourIncomeVariable.TotalAnnualIncome, null);
      trigger(YourIncomeVariable.TotalAnnualIncome);
      annualIncomeInputRef.current?.focus();
      return;
    }

    const totalAnnualIncome =
      choice === LowAnnualIncomeChoice.Annualized ? confirmation.annualizedIncome : confirmation.enteredIncome;

    if (choice === LowAnnualIncomeChoice.Annualized) {
      setValue(YourIncomeVariable.TotalAnnualIncome, totalAnnualIncome);
    }

    persistAndContinue(totalAnnualIncome);
  };

  const validateEmploymentFields = (errorMessage: string) => (value: any) => {
    const isNotEmployed = watch(YourIncomeVariable.NotEmployed);

    return isNotEmployed || (value !== null && value !== undefined && value !== '') ? true : errorMessage;
  };

  const validateEmploymentDateField = (errorMessage: string) => (value: string) => {
    if (watch(YourIncomeVariable.NotEmployed)) return true;
    if (!value) return errorMessage;

    const parsedDate = new Date(value);

    if (parsedDate < minDate) return `Date must be after ${formatMonthYear(minDate)}`;
    if (parsedDate > maxDate) return `Date must be before or equal to ${formatMonthYear(maxDate)}`;
  };

  const highestTierCredential = credentials ? getHighestTierCredential(credentials) : '';

  const validateStartOfEmployment = validateEmploymentDateField(
    getMessageForRequiredFields(YourIncomeInputLabel.StartOfEmployment),
  );
  const validateCredentialAcquisitionDate = validateEmploymentDateField(
    getMessageForRequiredFields(
      replaceCredentialPlaceholder(YourIncomeInputLabel.CredentialAcquisitionDate, highestTierCredential),
    ),
  );

  useEffect(() => {
    register(YourIncomeVariable.EmployerName, {
      validate:
        variant !== YourIncomeVariant.Default
          ? validateEmploymentFields(getMessageForRequiredFields(YourIncomeInputLabel.EmployerName))
          : undefined,
    });
    register(YourIncomeVariable.CreditScoreRange, {
      min: {
        message: `${YourIncomeInputLabel.CreditScoreRange} should be at least 350`,
        value: 350,
      },
      max: {
        message: `${YourIncomeInputLabel.CreditScoreRange} should be at most 850`,
        value: 850,
      },
    });
    register(YourIncomeVariable.TotalAnnualIncome, {
      required: getMessageForRequiredFields(YourIncomeInputLabel.GrossAnnualIncome),
    });
    if (variant !== YourIncomeVariant.CardWaitlist) {
      register(YourIncomeVariable.PayFrequency, {
        validate: validateEmploymentFields(getMessageForRequiredFields(YourIncomeInputLabel.PayFrequency)),
      });
    }
    register(YourIncomeVariable.NotEmployed);
    if (shouldCollectJobTitle) {
      register(YourIncomeVariable.JobTitle, {
        validate: validateEmploymentFields(getMessageForRequiredFields(YourIncomeInputLabel.JobTitle)),
      });
    }
    if (shouldShowCredentialAcquisitionDate) {
      register(YourIncomeVariable.CredentialAcquisitionDate, {
        validate: validateCredentialAcquisitionDate,
      });
    } else if (!credentials) {
      register(YourIncomeVariable.StartOfEmployment, {
        validate: validateStartOfEmployment,
      });
    }
  }, [register]);

  let title = '';
  let subtitle: string | JSX.Element = <></>;

  if (variant === YourIncomeVariant.CardWaitlist) {
    title = 'Help Improve Plannery';
    subtitle = (
      <>
        <p>Please enter some more details about you so we can better serve the nursing community.</p>
        <p>We never share or sell your data.</p>
      </>
    );
  } else if (currentFlow === CurrentFlow.Card || currentFlow === CurrentFlow.DebtConsolidation) {
    title = 'What’s your income?';
    subtitle = 'This information is needed to put together your offer.';
  } else {
    title = 'What’s your income?';
    subtitle = 'This will help us calculate your results.';
  }

  const getMonthYearPickerProps = () => {
    const variableKey = shouldShowCredentialAcquisitionDate
      ? YourIncomeVariable.CredentialAcquisitionDate
      : YourIncomeVariable.StartOfEmployment;

    const labelKey = shouldShowCredentialAcquisitionDate
      ? YourIncomeInputLabel.CredentialAcquisitionDate
      : YourIncomeInputLabel.StartOfEmployment;

    return {
      minDate,
      maxDate,
      name: variableKey,
      label:
        labelKey === YourIncomeInputLabel.CredentialAcquisitionDate
          ? replaceCredentialPlaceholder(labelKey, highestTierCredential)
          : labelKey,
      value: watcher[variableKey] ?? undefined,
      errorMessage: errors[variableKey]?.message,
      onChange: (date: string | undefined) => {
        setValue(variableKey, date);
        trigger(variableKey);
      },
    };
  };

  return (
    <>
      <FormNavigation
        {...navigationInfo}
        {...(variant === YourIncomeVariant.CardCrossSell && { step: 3, stepCount: 3 })}
      />
      <FormContainer title={title} subtitle={subtitle}>
        {variant === YourIncomeVariant.CardWaitlist && (
          <NumberInput
            label={YourIncomeInputLabel.CreditScoreRange}
            onChange={(option) => {
              setValue(YourIncomeVariable.CreditScoreRange, option.target.value);
              trigger(YourIncomeVariable.CreditScoreRange);
            }}
            placeholder="Enter a number"
            name={YourIncomeVariable.CreditScoreRange}
            errorMessage={errors[YourIncomeVariable.CreditScoreRange]?.message}
            value={watcher[YourIncomeVariable.CreditScoreRange] ?? undefined}
          />
        )}
        <NumberInput
          label={YourIncomeInputLabel.GrossAnnualIncome}
          inputMode="numeric"
          prefix="$"
          placeholder="$"
          errorMessage={errors[YourIncomeVariable.TotalAnnualIncome]?.message}
          thousandSeparator
          name={YourIncomeVariable.TotalAnnualIncome}
          onChange={(event) => {
            setValue(YourIncomeVariable.TotalAnnualIncome, getParsedNumericValue(event.target.value));
            trigger(YourIncomeVariable.TotalAnnualIncome);
          }}
          // NumberFormat goes uncontrolled on `undefined`, which would keep showing a cleared amount.
          value={
            watcher[YourIncomeVariable.TotalAnnualIncome] ? `${watcher[YourIncomeVariable.TotalAnnualIncome]}` : ''
          }
          maxLength={ANNUAL_INCOME_MAX_LENGTH}
          hint={
            isLowAnnualIncome(watcher[YourIncomeVariable.TotalAnnualIncome]) ? LOW_ANNUAL_INCOME_WARNING : undefined
          }
          hintVariant={NumberInputHintVariant.Warning}
          onKeyUp={(e) => e.key === 'Enter' && isValid && handleContinue()}
          autoFocus={variant !== YourIncomeVariant.CardWaitlist}
          getInputRef={(input: HTMLInputElement) => {
            annualIncomeInputRef.current = input;
          }}
        />
        {shouldShowAlimonyNote && <IncomeDisclaimerNote />}
        {variant !== YourIncomeVariant.Default && (
          <>
            <Input
              label={YourIncomeInputLabel.EmployerName}
              onChange={(event) => {
                setValue(YourIncomeVariable.EmployerName, event.target.value);
                trigger(YourIncomeVariable.EmployerName);
              }}
              onBlur={(event) => {
                setValue(YourIncomeVariable.EmployerName, event.target.value.trim());
                trigger(YourIncomeVariable.EmployerName);
              }}
              disabled={watcher[YourIncomeVariable.NotEmployed]}
              placeholder="Employer name"
              value={watcher[YourIncomeVariable.EmployerName]}
              errorMessage={errors[YourIncomeVariable.EmployerName]?.message}
              onKeyUp={(e) => e.key === 'Enter' && isValid && handleContinue()}
            />
            <CheckboxSmall
              label={YourIncomeInputLabel.NotCurrentlyEmployed}
              name={YourIncomeVariable.NotEmployed}
              onChange={(event) => {
                setValue(YourIncomeVariable.NotEmployed, event.target.checked);
                // Reset the rest of the fields
                setValue(YourIncomeVariable.EmployerName, '');
                setValue(YourIncomeVariable.CredentialAcquisitionDate, '');
                setValue(
                  YourIncomeVariable.PayFrequency,
                  event.target.checked
                    ? PayFrequencyOptionValue.Monthly
                    : initialPartner?.paymentFrequency ?? PayFrequencyOptionValue.BiWeekly,
                );
                setValue(YourIncomeVariable.StartOfEmployment, '');
                trigger([
                  YourIncomeVariable.NotEmployed,
                  YourIncomeVariable.EmployerName,
                  YourIncomeVariable.PayFrequency,
                  YourIncomeVariable.StartOfEmployment,
                  ...(shouldCollectJobTitle ? [YourIncomeVariable.JobTitle] : []),
                  ...(shouldShowCredentialAcquisitionDate ? [YourIncomeVariable.CredentialAcquisitionDate] : []),
                ]);
              }}
              checked={watcher[YourIncomeVariable.NotEmployed]}
            />
          </>
        )}
        {!watcher[YourIncomeVariable.NotEmployed] && (
          <>
            {watcher[YourIncomeVariable.EmployerName]!.length > 0 && variant !== YourIncomeVariant.CardWaitlist && (
              <InputSelect
                label={YourIncomeInputLabel.PayFrequency}
                options={PAY_FREQUENCY_OPTIONS}
                onChange={(option) => {
                  setValue(YourIncomeVariable.PayFrequency, option.value);
                  trigger(YourIncomeVariable.PayFrequency);
                }}
                placeholder="Pay Frequency"
                name={YourIncomeVariable.PayFrequency}
                value={watcher[YourIncomeVariable.PayFrequency]}
                errorMessage={errors[YourIncomeVariable.PayFrequency]?.message}
              />
            )}

            {(shouldShowCredentialAcquisitionDate || !credentials) && (
              <MonthYearPicker
                key={
                  shouldShowCredentialAcquisitionDate
                    ? YourIncomeVariable.CredentialAcquisitionDate
                    : YourIncomeVariable.StartOfEmployment
                }
                {...getMonthYearPickerProps()}
              />
            )}

            {shouldCollectJobTitle && (
              <InputSelect
                onChange={(option) => {
                  setValue(YourIncomeVariable.JobTitle, option.value);
                  trigger(YourIncomeVariable.JobTitle);
                }}
                className={styles.inputContainer}
                placeholder="Select"
                value={watcher[YourIncomeVariable.JobTitle]}
                label={YourIncomeInputLabel.JobTitle}
                onBlur={(event) => {
                  setValue(event.target.name as YourIncomeVariable, event.target.value);
                  trigger(event.target.name as YourIncomeVariable);
                }}
                options={[
                  ...benefitEligibleTitles.map((item) => ({ label: item, value: item })),
                  { label: 'Other', value: 'Other' },
                ]}
              />
            )}
          </>
        )}

        {variant === YourIncomeVariant.CardCrossSell && (
          <span className={styles.yourIncomeDisclaimer}>
            By clicking Apply, I hereby consent to the Plannery{' '}
            <a href="https://www.planneryapp.com/esign-agreement" target="_blank" rel="noreferrer">
              E-Sign Agreement
            </a>
            ,{' '}
            <a href="https://www.planneryapp.com/terms-of-service" target="_blank" rel="noreferrer">
              Terms of Service
            </a>
            , and{' '}
            <a href="https://www.planneryapp.com/communication-policy" target="_blank" rel="noreferrer">
              Communication Policy
            </a>{' '}
            and the{' '}
            <a href={FINWISE_PRIVACY_NOTICE_URL} target="_blank" rel="noreferrer">
              FinWise Bank Privacy Policy
            </a>
            , and I am providing written consent under the FCRA for Plannery to obtain consumer report information from
            my credit profile.
          </span>
        )}
        <Button className={styles.button} disabled={!isValid} onClick={handleContinue}>
          {variant === YourIncomeVariant.CardWaitlist ? 'Next' : 'Apply'}
        </Button>
      </FormContainer>
      {isLowIncomeConfirmOpen && lowIncomeConfirmation && (
        <LowAnnualIncomeConfirmModal
          confirmation={lowIncomeConfirmation}
          onClose={() => setIsLowIncomeConfirmOpen(false)}
          onConfirm={(choice) => handleLowIncomeConfirm(choice, lowIncomeConfirmation)}
        />
      )}
    </>
  );
};

export default YourIncome;
